import React, { useEffect, useState } from 'react';
import { Drawer, Form, Button, Table, DatePicker, InputNumber, Input, message } from 'antd';
import moment from 'moment';
import errorMessage from 'global_config/error_messages';
import ViewGeneratedReport from './generatedReport';
import {
	addQualityControlDataValue,
	fetchQualityControlDataList,
} from 'services/settings/qualityControl';
import HttpCodeMessage from 'shared_components/message_http_status';

const DataForm = ({ visible, onClose, record }) => {
	const [form] = Form.useForm();
	const [isShowGeneratedReport, setIsShowGeneratedReport] = useState(false);
	const [data, setData] = useState([]); // State to hold table data
	const [editedRecords, setEditedRecords] = useState({});

	useEffect(() => {
		if (record) {
			setData([record]); // Initialize the data with the passed record
		}
	}, [record]);

	useEffect(() => {
		fetchData();
	}, [record]);

	const handleInputChange = (e, record) => {
		const { value } = e.target;
		setEditedRecords(prev => ({
			...prev,
			[record.date]: value !== record.resultInterpretation,
		}));

		const updatedData = data.map(item =>
			item.date === record.date ? { ...item, resultInterpretation: value } : item,
		);
		setData(updatedData);
	};

	const editQualityControlDataValue = async value => {
		console.log('Edited record:', value);

		const LOGGEDIN_USER_DATA = sessionStorage.LOGGEDIN_USER_DATA
			? JSON.parse(sessionStorage.LOGGEDIN_USER_DATA)
			: null;
		const { givenName, middleName, lastName } = LOGGEDIN_USER_DATA;
		const initials = givenName.charAt(0) + middleName.charAt(0) + lastName.charAt(0);

		// Parse the date string into a moment object
		const parsedDate = moment(value.date, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');

		const payload = {
			manalyteid: record ? record.analyteid : null,
			mrundate: parsedDate, // Format the parsed date
			mresult: value.resultInterpretation,
			minitial: initials.toUpperCase(),
			mselected: '0',
		};

		console.log(payload);

		const response = await addQualityControlDataValue(payload);
		console.log(response);

		if (response.status === 200) {
			const httpMessageConfig = {
				message: 'Update successful!',
				status: response.status,
				duration: 3,
			};

			fetchData();
			HttpCodeMessage(httpMessageConfig);
		}
	};

	const fetchData = async () => {
		if (record) {
			const response = await fetchQualityControlDataList({ manalyteid: record.analyteid });
			console.log(response);
			if (response && response.data.records) {
				const formattedData = response.data.records.map(rec => ({
					date: moment(rec.rundate, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm'), // Keep the original format for display
					resultInterpretation: rec.result,
					medtech: rec.initial,
				}));
				setData(formattedData);
			} else {
				setData([]);
			}

			setEditedRecords({});
			form.resetFields();
		}
	};

	const onSubmit = async value => {
		const inputDate = value.rundate.format('YYYY-MM-DD HH:mm');
		const isDateExist = data.some(item => item.date === inputDate);

		if (isDateExist) {
			message.error('The entered date already exists in the table.');
			return;
		}

		const LOGGEDIN_USER_DATA = sessionStorage.LOGGEDIN_USER_DATA
			? JSON.parse(sessionStorage.LOGGEDIN_USER_DATA)
			: null;
		const { givenName, middleName, lastName } = LOGGEDIN_USER_DATA;
		const initials = givenName.charAt(0) + middleName.charAt(0) + lastName.charAt(0);

		const payload = {
			manalyteid: record ? record.analyteid : null,
			mrundate: value ? value.rundate.format('YYYY-MM-DD HH:mm') : null,
			mresult: value ? value.result : null,
			minitial: initials.toUpperCase(),
			mselected: '0',
		};

		console.log(payload);

		const response = await addQualityControlDataValue(payload);
		console.log(response);

		if (response.status === 200) {
			const httpMessageConfig = {
				message: 'Successfully created!',
				status: response.status,
				duration: 3,
			};

			fetchData();
			HttpCodeMessage(httpMessageConfig);
		}
	};

	const fieldRules = {
		required: [{ required: true, message: errorMessage.required }],
		numeric: [{ required: true, message: errorMessage.number }],
	};

	const columns = [
		{
			title: 'DATE',
			dataIndex: 'date',
			width: 150,
			text: 'center',
		},
		{
			title: 'RESULT INTERPRETATION',
			dataIndex: 'resultInterpretation',
			width: 150,
			render: (text, record) =>
				data.length > 0 && (
					<Input value={record.resultInterpretation} onChange={e => handleInputChange(e, record)} />
				),
		},
		{
			title: 'MED TECH',
			dataIndex: 'medtech',
			width: 150,
		},
		{
			title: 'ACTION',
			dataIndex: '',
			width: 100,
			render: record =>
				data.length > 0 && (
					<Button
						disabled={!editedRecords[record.date]}
						onClick={() => editQualityControlDataValue(record)}
					>
						SAVE
					</Button>
				),
		},
	];

	return (
		<Drawer
			title={`LOT NO. ${record ? record.lotno : null} | ${record ? record.sectionname : null} | ${
				record ? record.examname : null
			} | ${record ? record.levelname : null} | ${record ? record.level : null}`}
			width="50%"
			placement="right"
			closable
			onClose={() => onClose(false)}
			visible={visible}
		>
			<Form
				form={form}
				onFinish={onSubmit}
				className="quality-control-data-container"
				layout="vertical"
			>
				<div className="form">
					<Form.Item name="rundate" label="DATE" rules={fieldRules.required}>
						<DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
					</Form.Item>
					<Form.Item name="result" label="RESULT" rules={fieldRules.required}>
						<Input type="number" />
					</Form.Item>
					<Button htmlType="submit" shape="round" style={{ marginTop: '5px', width: 120 }}>
						SUBMIT
					</Button>
				</div>

				<Table
					style={{ marginTop: '10px' }}
					pagination={false}
					columns={columns}
					dataSource={data}
				/>

				<section className="drawerFooter">
					<div>
						<Button
							shape="round"
							style={{ margin: 10, width: 120 }}
							onClick={() => setIsShowGeneratedReport(true)}
						>
							PREVIEW
						</Button>
						<Button shape="round" style={{ margin: 10, width: 120 }} onClick={() => onClose(false)}>
							CLOSE
						</Button>
					</div>
				</section>
			</Form>

			<ViewGeneratedReport
				record={record}
				visible={isShowGeneratedReport}
				onClose={setIsShowGeneratedReport}
				data={data}
			/>
		</Drawer>
	);
};

export default DataForm;
